import React, { useState, useMemo, useEffect } from 'react';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';

import { Button } from 'react-scale/components/design-system/Button';
import { Modal, ModalBody, ModalFooter } from 'design-system/Modal';
import { TextInput, Select, Option, Slider } from 'react-scale/components/design-system';
import { Label, Form } from 'design-system/Form';
import { PartialRecord } from '@scale/shared/types/utils';
import { ReviewLevel } from '@scale/shared/defaults';
import { IInstructionItemJson } from '@scale/shared/types/instructionitem';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { makeStyles } from 'react-scale/components/design-system/styles';
import { IIMetricsData } from './types';
import { useAsyncFn, useUpdateEffect, usePrevious } from 'react-use-v13';
import SAlert from 'react-s-alert';
import { IThresholdValues } from '@scale/shared/types/corp/attemptAudit';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';

const THRESHOLD_STEP = 0.05;

const ATTEMPTER_BM_KEY: string = 'AllAttempterBMs';
const ATTEMPTER_BM_NAME: string = 'Use all attempter benchmarks (no ii filter)';

const useStyles = makeStyles({
  thresholdInput: {
    display: 'flex',
    flexFlow: 'row wrap',
  },
  thresholdLabel: {
    flex: '0 0 100%',
  },
  slider: {
    marginTop: 32,
    flex: '1 0 1%',
  },
  lookbackContainer: {
    borderRadius: 4,
    backgroundColor: '#f1f1f1',
    padding: 12,
  },
  lookbackInput: {
    backgroundColor: 'white',
  },
});

interface IThresholdFieldProps {
  speedAuditFilter?: boolean;
  batchIdToUpdate?: string;
  defaultInstrItemIds?: { [key: string]: number[] };
  reviewLevels?: ReviewLevel[];
  setReviewLevels?: (l: ReviewLevel[]) => void;
  manuallyExcludedReviewers?: string[];
  projectLabel?: string;
  taskCounts?: PartialRecord<ReviewLevel, number>;
  metricsData: IIMetricsData;
  instructionItems: IInstructionItemJson[];
  currentMinimumBenchmarksPerII?: { [key: string]: number };
  projectBatches?: { name: string; _id: string }[];
  onRecalculateMetrics: (lookbackDays: number) => void;
  usedLookbackDays: number;
  setThresholdValues?: (v: IThresholdValues) => void;
  setThresholdTasks?: (v: string) => void;
}

interface IQualityLabModalProps extends IThresholdFieldProps {
  isOpen: boolean;
  defaultReviewLevels?: ReviewLevel[];
  onClose: () => void;
  onUpdateAuditBatch?: (auditBatch: any, attemptIds: string[]) => Promise<void>;
  projectId: string;
}

export const ThresholdFields: React.FC<IThresholdFieldProps> = ({
  batchIdToUpdate,
  defaultInstrItemIds = {},
  reviewLevels,
  setReviewLevels,
  setThresholdTasks,
  manuallyExcludedReviewers,
  projectLabel,
  taskCounts,
  projectBatches,
  instructionItems,
  currentMinimumBenchmarksPerII = {},
  metricsData,
  onRecalculateMetrics,
  usedLookbackDays,
  setThresholdValues,
  speedAuditFilter,
}) => {
  const classes = useStyles();
  const iiNamesById: Record<string, string> = {
    ..._.mapValues(_.keyBy(instructionItems, '_id'), 'name'),
    ATTEMPTER_BM_KEY: ATTEMPTER_BM_NAME,
  };

  const [desiredQuality, setDesiredQuality] = useState<string>('.95');
  const [lookbackDays, setLookbackDays] = useState<number>(usedLookbackDays);
  const [batches, setBatches] = useState<string[]>([]);
  const [selectedInstrItemIds, setSelectedInstrItemIds] = useState<string[]>(
    Object.keys(defaultInstrItemIds),
  );

  const prevSelectedInstrItemIds = usePrevious(selectedInstrItemIds);
  const [instrItemThresholds, setInstrItemThresholds] = useState<{ [key: string]: number[] }>(
    defaultInstrItemIds,
  );
  const [minimumBenchmarksPerII, setMinimumBenchmarksPerII] = useState<{ [key: string]: number }>(
    currentMinimumBenchmarksPerII,
  );

  useUpdateEffect(() => {
    const addedInstrItems = _.difference(selectedInstrItemIds, prevSelectedInstrItemIds ?? []);
    const removedInstrItems = _.difference(prevSelectedInstrItemIds, selectedInstrItemIds);
    const newThresholds = { ...instrItemThresholds };
    removedInstrItems.forEach(removedId => delete newThresholds[removedId]);
    addedInstrItems.forEach(
      addedId => (newThresholds[addedId] = [valueDefaults[addedId]?.[0] ?? 0, 1]),
    );
    setInstrItemThresholds(newThresholds);
  }, [selectedInstrItemIds]);

  const setInstrItemThreshold = (instrItem: string, value: number[]) => {
    setInstrItemThresholds({
      ...instrItemThresholds,
      [instrItem]: value,
    });
  };

  const setMinimumBenchmarkPerII = (instrItem: string, value: number) => {
    setMinimumBenchmarksPerII({
      ...minimumBenchmarksPerII,
      [instrItem]: value,
    });
  };

  useEffect(() => {
    if (setThresholdValues) {
      const newThresholds = {
        instrItemThresholds,
        desiredQuality,
        minimumBenchmarksPerII,
        batches,
        lookbackDays,
      };
      setThresholdValues(newThresholds);
    }
  }, [instrItemThresholds, desiredQuality, minimumBenchmarksPerII, batches, lookbackDays]);

  const tasksFound = () => {
    const taskersPassingThreshold = _.pickBy(
      metricsData.filteredMetricsByWorker,
      (metrics, workerId) => {
        const counts = metricsData.filteredCountsByWorker?.[workerId];
        if (ATTEMPTER_BM_KEY in instrItemThresholds) {
          const threshold = instrItemThresholds[ATTEMPTER_BM_KEY];
          return (
            metrics[ATTEMPTER_BM_KEY] >= threshold[0] &&
            metrics[ATTEMPTER_BM_KEY] <= threshold[1] &&
            (counts && counts[ATTEMPTER_BM_KEY] && minimumBenchmarksPerII[ATTEMPTER_BM_KEY]
              ? counts[ATTEMPTER_BM_KEY] >= minimumBenchmarksPerII[ATTEMPTER_BM_KEY]
              : true)
          );
        } else {
          return (
            !_.includes(manuallyExcludedReviewers, workerId) &&
            _.every(
              instrItemThresholds,
              (threshold, ii) =>
                metrics[ii] >= threshold[0] &&
                metrics[ii] <= threshold[1] &&
                (counts && counts[ii] && minimumBenchmarksPerII?.[ii]
                  ? counts[ii] >= minimumBenchmarksPerII[ii]
                  : true),
            )
          );
        }
      },
    );

    const allTasks = _.compact(
      _.flatMap(Object.keys(taskersPassingThreshold), tasker => {
        const attempts = metricsData.attemptsByWorker[tasker];
        return attempts.map(attempt => {
          const reviewLevelCheck = reviewLevels?.includes(attempt.reviewLevel);
          const batchesCheck =
            batches.length > 0 ? attempt.batch && batches.includes(attempt.batch) : true;
          if (reviewLevelCheck && batchesCheck) {
            return attempt.task;
          }
        });
      }),
    );
    if (setThresholdTasks) setThresholdTasks(allTasks.toString());
    return allTasks.length;
  };

  const valueDefaults = useMemo(() => {
    // Slider default should be the average score for each id
    const metrics = Object.values(metricsData.filteredMetricsByWorker);
    const avgScoreEachII = _.fromPairs(
      selectedInstrItemIds.map(id => {
        if (defaultInstrItemIds[id]) {
          return defaultInstrItemIds[id];
        }
        const avgScore = _.mean(_.compact(metrics.map(scores => scores[id])));
        return [id, [parseFloat(avgScore.toFixed(2)), 1]];
      }),
    );
    return avgScoreEachII;
  }, [selectedInstrItemIds, defaultInstrItemIds, metricsData.filteredMetricsByWorker]);

  const marks = [
    {
      value: 0,
      label: 0,
    },
    {
      value: 1,
      label: 1,
    },
  ];

  const iiMinimumMarks = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(val => {
    return {
      value: val,
      label: val,
    };
  });

  const allAvailableBatches: { name: string; _id: string }[] = useMemo(() => {
    if (!projectBatches) {
      return [{ name: '', _id: '' }];
    }
    const attempts = _.flatMap(Object.values(metricsData.attemptsByWorker));
    const allAttemptBatches = _.uniq(attempts.map(attempt => attempt.batch));
    return projectBatches.filter(batch => allAttemptBatches.includes(batch._id));
  }, [metricsData.attemptsByWorker, projectBatches]);

  const renderBatchesValue = (v: unknown) => {
    const filters = v as any[];
    return filters.length
      ? `${filters.length} Batch${filters.length > 1 ? 'es' : ''} Selected`
      : 'All Batches';
  };

  const renderInstrIdPicker = (selectedInstrItemId: string) => {
    return (
      <div className={classes.thresholdInput} key={selectedInstrItemId}>
        <Label className={classes.thresholdLabel}>
          {iiNamesById[selectedInstrItemId]} Threshold
        </Label>
        <Slider
          className={classes.slider}
          defaultValue={[
            (defaultInstrItemIds[selectedInstrItemId] || valueDefaults[selectedInstrItemId])?.[0] ||
              0,
            (defaultInstrItemIds[selectedInstrItemId] || valueDefaults[selectedInstrItemId])?.[1] ||
              1,
          ]}
          valueLabelDisplay="on"
          onAdjust={(position: number[]) => setInstrItemThreshold(selectedInstrItemId, position)}
          minVal={0}
          maxVal={1}
          step={THRESHOLD_STEP}
          marks={marks}
        />
        <Label className={classes.thresholdLabel}>
          {iiNamesById[selectedInstrItemId]} Minimum Benchmarks per Tasker
        </Label>
        <Slider
          className={classes.slider}
          defaultValue={1}
          valueLabelDisplay="on"
          onAdjust={(position: number) => setMinimumBenchmarkPerII(selectedInstrItemId, position)}
          minVal={0}
          maxVal={10}
          step={1}
          marks={iiMinimumMarks}
        />
      </div>
    );
  };

  const allAttempterBMsOn = selectedInstrItemIds.includes(ATTEMPTER_BM_KEY);

  return (
    <>
      {!batchIdToUpdate && (
        <div className={classes.lookbackContainer}>
          <Label>Limit to Benchmark Scores From The Past N Days</Label>
          <TextInput
            className={classes.lookbackInput}
            type="number"
            value={lookbackDays}
            onChange={e => {
              setLookbackDays(parseInt(e.target.value));
            }}
          />
          {!speedAuditFilter && (
            <Button
              type="info"
              disabled={!!lookbackDays && lookbackDays === usedLookbackDays}
              style={{ marginLeft: 8 }}
              onClick={() => {
                onRecalculateMetrics(lookbackDays);
              }}
            >
              Update
            </Button>
          )}
        </div>
      )}
      {projectLabel && !speedAuditFilter && (
        <div>
          <Label>Project</Label>
          <TextInput disabled value={projectLabel} />
        </div>
      )}
      {!batchIdToUpdate && !speedAuditFilter && (
        <div>
          <Label>Review Levels</Label>
          <Select
            multiple={true}
            value={reviewLevels}
            onChange={e => {
              if (setReviewLevels) setReviewLevels(e.target.value as ReviewLevel[]);
            }}
          >
            <Option value={ReviewLevel.Corp} key={ReviewLevel.Corp}>
              {taskCounts && `L10 (${taskCounts[ReviewLevel.Corp] ?? 0} tasks)`}
            </Option>
            <Option value={ReviewLevel.Deliverable} key={ReviewLevel.Deliverable}>
              {taskCounts && `L12 (${taskCounts[ReviewLevel.Deliverable] ?? 0} tasks)`}
            </Option>
          </Select>
        </div>
      )}
      {!batchIdToUpdate && (
        <div>
          <Label>Batches</Label>
          <Select
            multiple={true}
            value={batches}
            renderValue={renderBatchesValue}
            onChange={e => setBatches(e.target.value as string[])}
            style={{ maxWidth: 400 }}
          >
            {allAvailableBatches.map(batch => (
              <Option value={batch._id} key={batch._id}>
                {batch.name}
              </Option>
            ))}
          </Select>
        </div>
      )}
      {!batchIdToUpdate && (
        <div>
          <Label>Desired Quality (Between 0-1)</Label>
          <TextInput
            type="number"
            value={desiredQuality}
            onChange={e => setDesiredQuality(e.target.value)}
          />
        </div>
      )}
      <div>
        <Label>Instruction Items</Label>
        <Select
          multiple={true}
          onChange={e => {
            setSelectedInstrItemIds(e.target.value as string[]);
          }}
          value={selectedInstrItemIds}
        >
          <Option value={ATTEMPTER_BM_KEY} key={ATTEMPTER_BM_KEY}>
            {ATTEMPTER_BM_NAME}
          </Option>
          {instructionItems.map(instructionItem => {
            return (
              <Option value={instructionItem._id} key={instructionItem._id}>
                {instructionItem.name}
              </Option>
            );
          })}
        </Select>
        {allAttempterBMsOn && (
          <div>
            <div>
              {'WARNING: \n' +
                ATTEMPTER_BM_NAME +
                ' is on. To re-enable II-based filtering, turn off ' +
                ATTEMPTER_BM_NAME +
                '.'}
            </div>
            {renderInstrIdPicker(ATTEMPTER_BM_KEY)}
          </div>
        )}
        {!allAttempterBMsOn &&
          selectedInstrItemIds &&
          selectedInstrItemIds.map(selectedInstrItemId => {
            return renderInstrIdPicker(selectedInstrItemId);
          })}
      </div>
      <p>Tasks found: {tasksFound()}</p>
    </>
  );
};

const QualityLabModal: React.FC<IQualityLabModalProps> = props => {
  const history = useHistory();

  const {
    defaultInstrItemIds,
    currentMinimumBenchmarksPerII,
    defaultReviewLevels,
    usedLookbackDays,
  } = props;
  const {
    projectId,
    batchIdToUpdate,
    manuallyExcludedReviewers,
    onUpdateAuditBatch,
    onClose,
    isOpen,
  } = props;

  const [
    { instrItemThresholds, desiredQuality, minimumBenchmarksPerII, batches },
    setThresholdValues,
  ] = useState<IThresholdValues>({
    instrItemThresholds: defaultInstrItemIds ?? {},
    batches: [],
    minimumBenchmarksPerII: currentMinimumBenchmarksPerII ?? {},
    desiredQuality: '.95',
    lookbackDays: usedLookbackDays,
  });
  const [reviewLevels, setReviewLevels] = useState<ReviewLevel[]>(defaultReviewLevels ?? []);
  const { isRemoAdmin } = useRemoAdminCtx();

  const [auditBatch, createAuditBatch] = useAsyncFn(async () => {
    const windowStartingAt = new Date(
      new Date().getTime() - usedLookbackDays * 1000 * 60 * 60 * 24,
    );

    const { auditBatchId } = await apiFetch<{ auditBatchId: string }>(
      null,
      `corp-api/quality/audits/${projectId}`,
      {
        method: 'POST',
        body: {
          desiredQuality: parseFloat(desiredQuality),
          instrItemThresholds,
          minimumBenchmarksPerII,
          subtaskBatches: batches,
          reviewLevels,
          windowStartingAt,
        },
        maxRetries: 1,
      },
    );
    if (isRemoAdmin || !history.push) {
      const url = new URL(window.location.href);
      url.pathname = '/remoadmin/tools/speed_audit';
      url.searchParams.set('batchId', auditBatchId);
      window.location.assign(url);
    } else {
      history.push(`/speed_audit?batchId=${auditBatchId}`);
    }
  }, [desiredQuality, instrItemThresholds, minimumBenchmarksPerII, batches, reviewLevels]);

  const [updateAuditBatch, editAuditBatch] = useAsyncFn(async () => {
    const { auditBatch: updateAuditBatch, attemptIds } = await apiFetch<{
      auditBatch: any;
      attemptIds: string[];
    }>(null, `corp-api/quality/audits/${batchIdToUpdate}`, {
      method: 'PUT',
      body: {
        instrItemThresholds,
        minimumBenchmarksPerII,
        manuallyExcludedReviewers,
      },
      maxRetries: 1,
    });
    onUpdateAuditBatch?.(updateAuditBatch, attemptIds);
    onClose();
    return { auditBatch: updateAuditBatch, attemptIds };
  }, [instrItemThresholds, minimumBenchmarksPerII, manuallyExcludedReviewers]);

  // Error handling
  useUpdateEffect(() => {
    if (auditBatch.error) {
      SAlert.error(`Error creating audit batch: ${auditBatch.error.message}`);
    }
    if (updateAuditBatch.error) {
      SAlert.error(`Error updating audit batch: ${updateAuditBatch.error.message}`);
    }
  });

  return (
    <Modal
      isOpen={isOpen}
      contentLabel="Setup Threshold Audit"
      onRequestClose={onClose}
      maxWidth="500px"
      zIndex={1000}
    >
      <ModalBody>
        <Form mode="vertical">
          <h1>Let's Find Some II Thresholds...</h1>
          <ThresholdFields
            setThresholdValues={setThresholdValues}
            reviewLevels={reviewLevels}
            setReviewLevels={setReviewLevels}
            {...props}
          />
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          type="primary"
          onClick={batchIdToUpdate ? editAuditBatch : createAuditBatch}
          disabled={auditBatch.loading || updateAuditBatch.loading}
        >
          {auditBatch.loading ? 'Preparing your audit...' : batchIdToUpdate ? 'Confirm' : 'Begin!'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default QualityLabModal;
